// Helpers puros para o calendario (Dia / Semana / Mes / Ano).
// Sem dependencias externas — usa apenas Date nativo.

const WEEKDAY_LABELS_SUNDAY_FIRST = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
const WEEKDAY_LABELS_MONDAY_FIRST = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"];

export function startOfDayLocal(value) {
  const date = value instanceof Date ? new Date(value) : new Date();
  date.setHours(0, 0, 0, 0);
  return date;
}

export function addDays(date, delta) {
  const next = new Date(date);
  next.setDate(next.getDate() + delta);
  return next;
}

export function addMonthsToDate(date, delta) {
  const next = new Date(date);
  next.setDate(1);
  next.setMonth(next.getMonth() + delta);
  const lastDay = new Date(next.getFullYear(), next.getMonth() + 1, 0).getDate();
  next.setDate(Math.min(date.getDate(), lastDay));
  return next;
}

export function addYearsToDate(date, delta) {
  const next = new Date(date);
  next.setFullYear(next.getFullYear() + delta);
  return next;
}

export function isSameDay(left, right) {
  if (!(left instanceof Date) || !(right instanceof Date)) return false;
  return (
    left.getFullYear() === right.getFullYear() &&
    left.getMonth() === right.getMonth() &&
    left.getDate() === right.getDate()
  );
}

export function isSameMonth(date, refYear, refMonth) {
  if (!(date instanceof Date)) return false;
  return date.getFullYear() === refYear && date.getMonth() === refMonth;
}

export function isSameYear(date, refYear) {
  if (!(date instanceof Date)) return false;
  return date.getFullYear() === refYear;
}

export function startOfWeek(date, weekStartsOn = 0) {
  const start = startOfDayLocal(date);
  const offset = (start.getDay() - weekStartsOn + 7) % 7;
  start.setDate(start.getDate() - offset);
  return start;
}

export function endOfWeek(date, weekStartsOn = 0) {
  const start = startOfWeek(date, weekStartsOn);
  return addDays(start, 6);
}

/**
 * Constroi grid de 42 dias (6 semanas x 7 dias) cobrindo o mes.
 */
export function buildMonthGrid(year, month, weekStartsOn = 0) {
  const firstOfMonth = new Date(year, month, 1);
  firstOfMonth.setHours(0, 0, 0, 0);

  const firstDayWeekday = firstOfMonth.getDay();
  const offset = (firstDayWeekday - weekStartsOn + 7) % 7;

  const start = new Date(firstOfMonth);
  start.setDate(start.getDate() - offset);

  const cells = [];
  for (let i = 0; i < 42; i += 1) {
    const date = new Date(start);
    date.setDate(start.getDate() + i);
    cells.push({
      date,
      inMonth: date.getMonth() === month && date.getFullYear() === year,
      key: dayKey(date),
    });
  }
  return cells;
}

/**
 * Constroi os 7 dias da semana que contem `date`.
 */
export function buildWeekDays(date, weekStartsOn = 0) {
  const start = startOfWeek(date, weekStartsOn);
  const days = [];
  for (let i = 0; i < 7; i += 1) {
    const cell = addDays(start, i);
    days.push({ date: cell, key: dayKey(cell) });
  }
  return days;
}

/**
 * Constroi os 12 meses de um ano, cada um com seu grid de 42 dias.
 */
export function buildYearMonths(year, weekStartsOn = 0) {
  const months = [];
  for (let m = 0; m < 12; m += 1) {
    months.push({
      year,
      month: m,
      cells: buildMonthGrid(year, m, weekStartsOn),
    });
  }
  return months;
}

export function getWeekdayLabels(weekStartsOn = 0) {
  return weekStartsOn === 1
    ? WEEKDAY_LABELS_MONDAY_FIRST
    : WEEKDAY_LABELS_SUNDAY_FIRST;
}

export function formatMonthLabel(year, month) {
  const date = new Date(year, month, 1);
  const text = date.toLocaleDateString("pt-BR", {
    month: "long",
    year: "numeric",
  });
  return text.charAt(0).toUpperCase() + text.slice(1);
}

export function formatMonthShort(year, month) {
  const date = new Date(year, month, 1);
  const text = date.toLocaleDateString("pt-BR", { month: "short" });
  return text.charAt(0).toUpperCase() + text.slice(1).replace(".", "");
}

export function formatDayLabel(date) {
  return date.toLocaleDateString("pt-BR", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

export function formatWeekRangeLabel(start, end) {
  const sameMonth =
    start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear();
  if (sameMonth) {
    const month = start.toLocaleDateString("pt-BR", { month: "long" });
    return `${start.getDate()} – ${end.getDate()} de ${month} de ${start.getFullYear()}`;
  }
  const sLabel = start.toLocaleDateString("pt-BR", { day: "numeric", month: "short" });
  const eLabel = end.toLocaleDateString("pt-BR", { day: "numeric", month: "short" });
  return `${sLabel} – ${eLabel} de ${end.getFullYear()}`;
}

export function formatWeekdayShort(date) {
  const text = date.toLocaleDateString("pt-BR", { weekday: "short" });
  return text.charAt(0).toUpperCase() + text.slice(1).replace(".", "");
}

export function dayKey(date) {
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
}

/**
 * Agrupa eventos por chave de dia local (YYYY-M-D).
 */
export function groupEventsByDay(events) {
  const map = new Map();
  events.forEach((event) => {
    if (!(event.date instanceof Date) || Number.isNaN(event.date.getTime())) return;
    const key = dayKey(event.date);
    const list = map.get(key);
    if (list) list.push(event);
    else map.set(key, [event]);
  });
  return map;
}

/**
 * Agrupa os eventos de UM dia por `groupLabel` (ex.: cliente), preservando a
 * ordem de primeira aparição — para o calendário mostrar o cliente uma vez
 * com seus ambientes em sub-lista (F11). Eventos sem groupLabel viram grupo
 * próprio (label null) — fallback retrocompatível.
 * @returns {Array<{ chave: string, label: string|null, eventos: Array }>}
 */
export function agruparEventosPorCliente(eventos) {
  const grupos = [];
  const indice = new Map();
  (eventos || []).forEach((ev) => {
    const temLabel = ev.groupLabel != null && ev.groupLabel !== "";
    const chave = temLabel ? `g:${ev.groupLabel}` : `s:${ev.id}`;
    let grupo = indice.get(chave);
    if (!grupo) {
      grupo = { chave, label: temLabel ? ev.groupLabel : null, eventos: [] };
      indice.set(chave, grupo);
      grupos.push(grupo);
    }
    grupo.eventos.push(ev);
  });
  return grupos;
}

/**
 * Limita os grupos a no máximo `maxAmbientes` ambientes no total — para as
 * células apertadas da visão de Mês. Mostra grupos inteiros até estourar o
 * orçamento (o último pode sair truncado) e devolve quantos ambientes
 * ficaram ocultos.
 * @returns {{ gruposVisiveis: Array, ocultos: number }}
 */
export function limitarGruposPorAmbientes(grupos, maxAmbientes) {
  const totalAmbientes = grupos.reduce((soma, g) => soma + g.eventos.length, 0);
  if (totalAmbientes <= maxAmbientes) {
    return { gruposVisiveis: grupos, ocultos: 0 };
  }
  const gruposVisiveis = [];
  let usados = 0;
  for (const grupo of grupos) {
    if (usados >= maxAmbientes) break;
    const resta = maxAmbientes - usados;
    if (grupo.eventos.length <= resta) {
      gruposVisiveis.push(grupo);
      usados += grupo.eventos.length;
    } else {
      gruposVisiveis.push({ ...grupo, eventos: grupo.eventos.slice(0, resta) });
      usados = maxAmbientes;
    }
  }
  return { gruposVisiveis, ocultos: totalAmbientes - usados };
}
